// THE COMMAND DECK's painter (#6778, prototype `deck.draw`, `:3858-3953`), cut by layer:
// the backdrop (grid, column washes, stage numbers and labels, ruler), the lanes (intake
// and machines), the scan line, the gate flashes, the pills, and the sparks. Each reads
// the geometry `fleet-live-deck.ts` placed and the palette `fleet-live-palette.ts` read.
//
// `motion: false` (reduced motion) is a STILL FRAME: positions snap, and no scan line,
// flash, arc, trail, ping or spark is drawn (owner ruling V9 #1).

import { FLEET_LIVE_STAGES } from '@developerz/domain';
import { t } from '../i18n';
import type { FleetLiveModel, LiveItem, LiveMachine } from './fleet-live';
import {
  alpha,
  brackets,
  cut,
  MONO,
  type Paint2D,
  paintSparks,
  roundRect,
} from './fleet-live-canvas';
import {
  DECK_TOP,
  type DeckGeo,
  type DeckLayout,
  EDGE_PAD,
  fitText,
  INTAKE_LANES,
  laneY,
  layoutLanes,
  measurer,
  type OverflowPill,
  PILL_H,
  PILL_R,
  type PlacedPill,
  stageLabelLines,
} from './fleet-live-deck';
import {
  burst,
  MOTION_MS,
  type Motion,
  type MotionEffect,
  type Particle,
  stepParticles,
} from './fleet-live-motion';
import { type LivePalette, toneColor } from './fleet-live-palette';

/** The deck's scan line sweeps once every 7 s (prototype `:3898`). */
export const SCAN_MS = 7000;

export const PILL_FONT = `500 11px ${MONO}`;

export type DeckFrame = {
  readonly g: DeckGeo;
  readonly p: LivePalette;
  readonly model: FleetLiveModel;
  readonly motion: Motion | null;
  readonly now: number;
  readonly dt: number;
  readonly hover: string | null;
};

/** The CPU bar's and ring's colour: neutral, amber at 70% or more, red above 90%. */
export function cpuColor(p: LivePalette, cpu: number, neutral: string): string {
  if (cpu > 90) return p.red;
  if (cpu >= 70) return p.amber;
  return neutral;
}

function paintColumns(ctx: Paint2D, g: DeckGeo, p: LivePalette): void {
  const measure = measurer(ctx, `500 ${g.compact ? 9 : 10}px ${MONO}`);
  for (let s = 0; s < FLEET_LIVE_STAGES.length; s += 1) {
    const cx = g.x0 + s * g.cw;
    if (s % 2 === 1) {
      ctx.fillStyle = alpha(p.live, p.dark ? 0.018 : 0.025);
      ctx.fillRect(cx, DECK_TOP - 6, g.cw, g.h - DECK_TOP);
    }
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = alpha(p.live, 0.85);
    ctx.font = `500 9px ${MONO}`;
    ctx.fillText(`0${s + 1}`, cx + 6, 14);
    const lines = stageLabelLines(g, s, measure);
    ctx.fillStyle = p.t2;
    ctx.font = `500 ${g.compact ? 9 : 10}px ${MONO}`;
    lines.forEach((line, i) => {
      ctx.fillText(line, cx + 6, 27 + i * 11);
    });
  }
}

function paintBackdrop(ctx: Paint2D, g: DeckGeo, p: LivePalette): void {
  ctx.clearRect(0, 0, g.w, g.h + 8);
  ctx.fillStyle = p.grid;
  for (let x = g.x0; x < g.x1; x += 16)
    for (let y = DECK_TOP; y < g.h; y += 16) ctx.fillRect(x, y, 1, 1);
  paintColumns(ctx, g, p);
  ctx.strokeStyle = p.rule;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let i = 0; i <= 80; i += 1) {
    const x = Math.round(g.x0 + (i * g.cw) / 10) + 0.5;
    const th = i % 10 === 0 ? 10 : i % 5 === 0 ? 6 : 3;
    ctx.moveTo(x, DECK_TOP - 4);
    ctx.lineTo(x, DECK_TOP - 4 + th);
  }
  ctx.stroke();
  for (let s = 0; s <= FLEET_LIVE_STAGES.length; s += 1) {
    const x = Math.round(g.x0 + s * g.cw) + 0.5;
    const edge = s === 0 || s === FLEET_LIVE_STAGES.length;
    ctx.strokeStyle = edge ? p.line : alpha(p.text, p.dark ? 0.1 : 0.12);
    ctx.beginPath();
    ctx.moveTo(x, DECK_TOP - 4);
    ctx.lineTo(x, g.h);
    ctx.stroke();
  }
}

/** A machine lane's label column: the live dot, the name, and the CPU bar when reported. */
function paintMachine(ctx: Paint2D, g: DeckGeo, p: LivePalette, m: LiveMachine, y: number): void {
  const dot = m.busy ? p.live : p.t3;
  ctx.fillStyle = alpha(dot, m.busy ? 0.35 : 0.15);
  ctx.beginPath();
  ctx.arc(16, y, 5, 0, 7);
  ctx.fill();
  ctx.fillStyle = dot;
  ctx.beginPath();
  ctx.arc(16, y, 2.4, 0, 7);
  ctx.fill();
  ctx.font = `500 ${g.compact ? 9.5 : 10.5}px ${MONO}`;
  ctx.fillStyle = m.busy ? p.text : p.t2;
  ctx.textAlign = 'left';
  ctx.fillText(cut(m.name, g.compact ? 9 : 13), 28, y - (g.lane > 28 ? 5 : 4));
  // An unreported CPU draws no bar and no number (V7: omitted, never simulated).
  if (m.cpu === null) return;
  const bw = g.compact ? 34 : 52;
  const by = y + (g.lane > 28 ? 7 : 6);
  ctx.fillStyle = alpha(p.text, 0.08);
  ctx.fillRect(28, by - 1, bw, 2);
  ctx.fillStyle = cpuColor(p, m.cpu, alpha(p.text, 0.45));
  ctx.fillRect(28, by - 1, (bw * Math.min(100, m.cpu)) / 100, 2);
  ctx.fillStyle = p.t3;
  ctx.font = `400 9.5px ${MONO}`;
  ctx.fillText(`${Math.round(m.cpu)}%`, 28 + bw + 6, by);
}

function paintLanes(
  ctx: Paint2D,
  g: DeckGeo,
  p: LivePalette,
  machines: readonly LiveMachine[],
): void {
  ctx.textBaseline = 'middle';
  const rows = INTAKE_LANES + machines.length;
  for (let r = 0; r < rows; r += 1) {
    const y = Math.round(laneY(g, r)) + 0.5;
    ctx.strokeStyle = r < INTAKE_LANES ? p.rule : p.line;
    ctx.beginPath();
    if (r < INTAKE_LANES) {
      ctx.moveTo(g.x0, y);
      ctx.lineTo(g.x0 + 4 * g.cw, y);
    } else {
      ctx.moveTo(g.x0 + 3 * g.cw, y);
      ctx.lineTo(g.x1, y);
    }
    ctx.stroke();
    if (r === 1) {
      ctx.font = `500 ${g.compact ? 9.5 : 10.5}px ${MONO}`;
      ctx.fillStyle = p.t3;
      ctx.textAlign = 'left';
      ctx.fillText(t('fleet.live.intake').toUpperCase(), 12, y);
    }
    const m = r >= INTAKE_LANES ? machines[r - INTAKE_LANES] : undefined;
    if (m !== undefined) paintMachine(ctx, g, p, m, y);
  }
  ctx.fillStyle = p.t3;
  ctx.textAlign = 'left';
  ctx.font = `500 10px ${MONO}`;
  if (machines.length > 0) {
    ctx.fillText(
      t('fleet.live.machines').toUpperCase(),
      12,
      laneY(g, INTAKE_LANES) - g.lane / 2 - 7,
    );
  }
  ctx.strokeStyle = alpha(p.text, p.dark ? 0.22 : 0.25);
  ctx.beginPath();
  for (let gi = 0; gi <= 8; gi += 1) {
    for (let ri = 0; ri < rows; ri += 1) {
      if (ri < INTAKE_LANES ? gi > 4 : gi < 3) continue;
      const x = Math.round(g.x0 + gi * g.cw) + 0.5;
      const y = Math.round(laneY(g, ri)) + 0.5;
      ctx.moveTo(x - 3, y);
      ctx.lineTo(x + 4, y);
    }
  }
  ctx.stroke();
}

function scanX(g: DeckGeo, now: number): number {
  return g.x0 - 60 + ((now % SCAN_MS) / SCAN_MS) * (g.x1 - g.x0 + 120);
}

function paintScan(ctx: Paint2D, g: DeckGeo, p: LivePalette, sx: number): void {
  const grad = ctx.createLinearGradient(sx - 90, 0, sx, 0);
  grad.addColorStop(0, alpha(p.live, 0));
  grad.addColorStop(1, alpha(p.live, p.dark ? 0.07 : 0.06));
  ctx.fillStyle = grad;
  ctx.fillRect(sx - 90, DECK_TOP - 4, 90, g.h - DECK_TOP + 4);
  ctx.strokeStyle = alpha(p.glow, p.dark ? 0.35 : 0.4);
  ctx.beginPath();
  ctx.moveTo(Math.round(sx) + 0.5, DECK_TOP - 4);
  ctx.lineTo(Math.round(sx) + 0.5, g.h);
  ctx.stroke();
}

/** Per-item eased position, its velocity (for the trail) and its scan-line ping. */
type Eased = { x: number; y: number; v: number; ping: number };

function paintFlash(
  ctx: Paint2D,
  p: LivePalette,
  e: MotionEffect,
  g: DeckGeo,
  y: number,
  now: number,
): void {
  const k = (now - e.at) / MOTION_MS.gate;
  if (k < 0 || k >= 1) return;
  const x = g.x0 + (e.kind === 'ci' ? 6 : e.stage) * g.cw;
  const col = e.kind === 'ci' ? p.red : e.stage >= 6 ? p.green : p.live;
  ctx.strokeStyle = alpha(col, (1 - k) * 0.9);
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(Math.round(x) + 0.5, y - 12);
  ctx.lineTo(Math.round(x) + 0.5, y + 12);
  ctx.stroke();
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(x, y, 4 + k * 14, 0, 7);
  ctx.stroke();
}

function paintArc(
  ctx: Paint2D,
  g: DeckGeo,
  p: LivePalette,
  item: LiveItem,
  y: number,
  k: number,
  tiny: boolean,
): void {
  const xg = g.x0 + 6 * g.cw;
  const xp = g.x0 + 4.35 * g.cw;
  ctx.setLineDash([3, 3]);
  ctx.strokeStyle = alpha(p.red, (1 - k) * 0.75);
  ctx.beginPath();
  ctx.moveTo(xg, y - 3);
  ctx.bezierCurveTo(xg - 10, y - 26, xp + 10, y - 26, xp, y - 3);
  ctx.stroke();
  ctx.setLineDash([]);
  if (tiny) return;
  ctx.fillStyle = alpha(p.red, 1 - k);
  ctx.font = `500 9.5px ${MONO}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  const caption = t('fleet.live.ci_caption', { count: item.ciRounds ?? 0 }).toUpperCase();
  ctx.fillText(caption, (xg + xp) / 2, y - 24);
}

function pillColor(p: LivePalette, item: LiveItem, ciAt: number | undefined, now: number): string {
  if (ciAt !== undefined && now - ciAt < MOTION_MS.tint && item.state === 'running') return p.red;
  return toneColor(p, item.tone);
}

/** A caption above a pill, cut so it ends before the canvas's right edge. */
function caption(ctx: Paint2D, text: string, x: number, y: number, right: number): void {
  ctx.fillText(
    fitText(text, right - x, (s) => ctx.measureText(s).width),
    x,
    y,
  );
}

function paintCaptions(
  ctx: Paint2D,
  p: LivePalette,
  o: { readonly pill: PlacedPill; readonly x: number; readonly y: number; readonly right: number },
  col: string,
  tiny: boolean,
): void {
  const { pill, x, y, right } = o;
  const { item } = pill;
  if (item.state === 'blocked' || item.state === 'parked') {
    ctx.strokeStyle = alpha(col, 0.9);
    brackets(ctx, x - 4, y - 4, pill.w + 8, PILL_H + 8);
    ctx.font = `600 9.5px ${MONO}`;
    ctx.fillStyle = col;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    const id = item.blocker?.id ?? '';
    const key =
      item.state === 'blocked' ? 'fleet.live.blocked_caption' : 'fleet.live.parked_caption';
    caption(ctx, t(key, { id }).trim().toUpperCase(), x, y - 7, right);
  }
  if (item.state === 'waiting' && !tiny) {
    ctx.font = `500 9.5px ${MONO}`;
    ctx.fillStyle = p.t3;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    caption(ctx, t('fleet.live.waiting_caption').toUpperCase(), x, y - 6, right);
  }
}

type PillPaint = {
  pill: PlacedPill;
  at: Eased;
  col: string;
  fade: number;
  lit: number;
  /** The canvas's right edge less its pad: captions end before it. */
  right: number;
};

function paintPill(
  ctx: Paint2D,
  p: LivePalette,
  o: PillPaint,
  tiny: boolean,
  motion: boolean,
): void {
  const { pill, at, col, fade, lit, right } = o;
  const tl = Math.min(110, Math.max(0, at.v * 900));
  if (motion && tl > 4 && pill.item.state === 'running') {
    const grad = ctx.createLinearGradient(at.x - tl, 0, at.x, 0);
    grad.addColorStop(0, alpha(col, 0));
    grad.addColorStop(1, alpha(col, 0.55 * fade));
    ctx.strokeStyle = grad;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(at.x - tl, at.y);
    ctx.lineTo(at.x, at.y);
    ctx.stroke();
    ctx.lineWidth = 1;
  }
  const x = at.x - 10;
  const y = at.y - PILL_H / 2;
  roundRect(ctx, x, y, pill.w, PILL_H, PILL_R);
  ctx.fillStyle = alpha(p.fill, fade);
  ctx.fill();
  ctx.strokeStyle = alpha(col, (0.45 + 0.45 * lit) * fade);
  ctx.stroke();
  ctx.fillStyle = alpha(col, 0.25 * fade);
  ctx.beginPath();
  ctx.arc(at.x, at.y, 5.5, 0, 7);
  ctx.fill();
  ctx.fillStyle = alpha(col, fade);
  ctx.beginPath();
  ctx.arc(at.x, at.y, 3, 0, 7);
  ctx.fill();
  ctx.font = PILL_FONT;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillStyle = alpha(p.text, (fade < 1 ? 0.75 : 0.92) * fade);
  ctx.fillText(pill.text, at.x + 9, at.y + 0.5);
  if (fade === 1) paintCaptions(ctx, p, { pill, x, y, right }, col, tiny);
}

export type DeckPainter = {
  /** Paint one frame; answers the pills and "+N" pills drawn (the tooltip's hit lists). */
  readonly paint: (ctx: Paint2D, f: DeckFrame) => DeckLayout;
};

type DeckState = {
  eased: Map<string, Eased>;
  next: Map<string, Eased>;
  sparks: Particle[];
  readonly burstSeen: Set<number>;
  lastPills: Map<string, PlacedPill>;
};

function easeTo(s: DeckState, pill: PlacedPill, f: DeckFrame, sx: number): Eased {
  const k = f.motion === null ? 1 : Math.min(1, f.dt * 0.01);
  const st = s.eased.get(pill.item.id) ?? { x: pill.x, y: pill.y, v: 0, ping: 0 };
  const ox = st.x;
  st.x += (pill.x - st.x) * k;
  st.y += (pill.y - st.y) * k;
  st.v = st.v * 0.85 + (f.dt ? (st.x - ox) / f.dt : 0) * 0.15;
  if (f.motion !== null && Math.abs(st.x - sx) < 6) st.ping = 1;
  st.ping = f.motion === null ? 0 : Math.max(0, st.ping - f.dt / 900);
  s.next.set(pill.item.id, st);
  return st;
}

/** Gate flashes and ship bursts, then the finished items fading out. */
function paintEffects(ctx: Paint2D, f: DeckFrame, motion: Motion, s: DeckState): void {
  const { g, p, now } = f;
  const tiny = g.cw < 74;
  const live = new Set(motion.effects.map((e) => e.seq));
  for (const seq of s.burstSeen) if (!live.has(seq)) s.burstSeen.delete(seq);
  for (const e of motion.effects) {
    const at = s.eased.get(e.id);
    if (at === undefined) continue;
    if (e.kind === 'ship' && !s.burstSeen.has(e.seq)) {
      s.burstSeen.add(e.seq);
      s.sparks.push(...burst(at.x, at.y, 18));
    }
    paintFlash(ctx, p, e, g, at.y, now);
  }
  for (const gone of motion.gone) {
    const last = s.lastPills.get(gone.item.id);
    const st = s.eased.get(gone.item.id);
    const fade = Math.max(0, 1 - (now - gone.at) / MOTION_MS.fade);
    if (last === undefined || st === undefined || fade <= 0) continue;
    s.next.set(gone.item.id, st);
    const col = toneColor(p, gone.item.tone);
    paintPill(ctx, p, { pill: last, at: st, col, fade, lit: 0, right: g.w - EDGE_PAD }, tiny, true);
  }
}

function paintLivePills(
  ctx: Paint2D,
  f: DeckFrame,
  pills: readonly PlacedPill[],
  s: DeckState,
): void {
  const { g, p, motion, now } = f;
  const tiny = g.cw < 74;
  const sx = scanX(g, now);
  for (const pill of pills) {
    const st = easeTo(s, pill, f, sx);
    const ci = motion?.ciAt.get(pill.item.id);
    if (motion !== null && ci !== undefined && now - ci < MOTION_MS.arc) {
      paintArc(ctx, g, p, pill.item, st.y, (now - ci) / MOTION_MS.arc, tiny);
    }
    const col = pillColor(p, pill.item, ci, now);
    const lit = Math.max(st.ping, f.hover === pill.item.id ? 1 : 0);
    paintPill(
      ctx,
      p,
      { pill, at: st, col, fade: 1, lit, right: g.w - EDGE_PAD },
      tiny,
      motion !== null,
    );
  }
}

/** A lane's "+N" pill: a dashed outline and its count, at the lane's left edge. */
function paintOverflow(ctx: Paint2D, p: LivePalette, o: OverflowPill, lit: boolean): void {
  roundRect(ctx, o.x, o.y - PILL_H / 2, o.w, PILL_H, PILL_R);
  ctx.fillStyle = p.fill;
  ctx.fill();
  ctx.strokeStyle = lit ? p.text : alpha(p.text, 0.45);
  ctx.setLineDash([3, 3]);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.font = PILL_FONT;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillStyle = p.t2;
  ctx.fillText(o.text, o.x + o.w / 2, o.y + 0.5);
  ctx.textAlign = 'left';
}

/** One deck's painter, holding its eased positions and sparks between frames. */
export function createDeckPainter(): DeckPainter {
  const s: DeckState = {
    eased: new Map(),
    next: new Map(),
    sparks: [],
    burstSeen: new Set(),
    lastPills: new Map(),
  };
  return {
    paint(ctx, f) {
      const { g, p, model, motion, now } = f;
      paintBackdrop(ctx, g, p);
      paintLanes(ctx, g, p, model.machines);
      if (motion !== null) paintScan(ctx, g, p, scanX(g, now));
      const { pills, overflow } = layoutLanes(g, model.items, measurer(ctx, PILL_FONT));
      s.next = new Map();
      if (motion !== null) paintEffects(ctx, f, motion, s);
      paintLivePills(ctx, f, pills, s);
      s.sparks = motion === null ? [] : stepParticles(s.sparks, f.dt);
      for (const o of overflow) paintOverflow(ctx, p, o, f.hover === o.key);
      paintSparks(ctx, p.green, s.sparks);
      s.eased = s.next;
      s.lastPills = new Map(pills.map((pl) => [pl.item.id, pl]));
      return {
        pills: pills.map((pl) => {
          const st = s.eased.get(pl.item.id);
          return st === undefined ? pl : { ...pl, x: st.x, y: st.y };
        }),
        overflow,
      };
    },
  };
}
